import type { BillingBannerStatus } from '../types'

export type BannerTone = 'info' | 'warning' | 'critical'

export interface BillingNotice {
  id: 'read-only' | 'past-due' | 'trial' | 'storage'
  tone: BannerTone
  title: string
  body: string
  /** Only for someone who can open the billing page; staff are told who to ask. */
  cta?: { label: string; to: string }
  /** A read-only workspace and a trial in its last week cannot be closed. */
  dismissible: boolean
}

const ASK_ADMIN = 'Ask an administrator of this workspace to sort out billing.'

/**
 * What the app-shell strip says about billing, from the server's status.
 *
 * `canManage` is whether this person may open the billing page (the same
 * permission the route uses). Staff see only what explains a refused write or
 * one that is days away; prices and seats are never shown to them.
 */
export function billingNotices(
  status: BillingBannerStatus | undefined,
  canManage: boolean
): BillingNotice[] {
  if (!status || status.access === 'exempt') return []
  const notices: BillingNotice[] = []
  const toBilling = (label: string) => (canManage ? { label, to: '/billing' } : undefined)

  if (status.access === 'restricted') {
    const unpaid = status.status === 'unpaid'
    const body =
      status.reason === 'trial_ended'
        ? 'The free trial has ended, so this workspace is read-only, including clocking in. ' +
          'Nothing has been deleted. Choose a plan to make changes again.'
        : unpaid
          ? 'The payment could not be taken after several attempts, so this workspace is ' +
            'read-only, including clocking in. Paying the open invoice restores it at once.'
          : 'The subscription has ended, so this workspace is read-only, including clocking in. ' +
            'Nothing has been deleted. Start a plan again to make changes.'
    notices.push({
      id: 'read-only',
      tone: 'critical',
      title: 'This workspace is read-only',
      body: canManage
        ? body
        : `This workspace is read-only, including clocking in. Nothing has been deleted. ${ASK_ADMIN}`,
      cta: toBilling(unpaid ? 'Pay the open invoice' : 'Choose a plan'),
      dismissible: false,
    })
    return notices
  }

  if (status.status === 'past_due' && canManage) {
    notices.push({
      id: 'past-due',
      tone: 'warning',
      title: 'Payment failed',
      body:
        'Nothing has stopped working yet. Stripe will retry the card over the next few ' +
        'weeks. Update it to keep the workspace from becoming read-only.',
      cta: toBilling('Update payment'),
      dismissible: true,
    })
  }

  // The workspace's own trial, before any Checkout. A Stripe trial already has
  // a card on file, so it needs no countdown.
  const days = status.trialDaysLeft
  if (status.access === 'trialing' && !status.hasSubscription && days !== null) {
    const lastWeek = days <= 7
    if (canManage || lastWeek) {
      const when = days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`
      notices.push({
        id: 'trial',
        tone: days <= 1 ? 'critical' : lastWeek ? 'warning' : 'info',
        title: days <= 1 ? `Your free trial ends ${when}` : `${days} days left in your free trial`,
        body: canManage
          ? `Choose a plan before the trial ends ${when}, or the workspace becomes read-only, including clocking in.`
          : `The workspace becomes read-only when the trial ends ${when}. ${ASK_ADMIN}`,
        cta: toBilling('Choose a plan'),
        dismissible: !lastWeek,
      })
    }
  }

  if (canManage && (status.storage.overCap || status.storage.nearCap)) {
    notices.push({
      id: 'storage',
      tone: 'warning',
      title: status.storage.overCap
        ? 'Storage allowance exceeded'
        : 'Storage allowance nearly used',
      body: status.storage.overCap
        ? 'Files take more space than your plan includes for your active users. ' +
          'Nothing is blocked yet. Remove old files or move to a larger plan.'
        : 'Files use over 90% of the space your plan includes for your active users.',
      cta: toBilling('See plans'),
      dismissible: true,
    })
  }

  return notices
}
